<template>
    <div>
        <h2>{{ $t("settings.smart-routing.contact-data.header") }}</h2>

        <b-form-group
            id="fieldset-contact-match-mode"
            :description="$t('settings.smart-routing.contact-data.match-mode.description')"
            :label="$t('settings.smart-routing.contact-data.match-mode.label')"
            content-cols-lg="7"
            content-cols-sm
            label-cols-lg="3"
            label-cols-sm="4"
        >
            <b-form-radio-group
                id="contact-match-mode-radio"
                v-model="currentRoute.contactDetailsMatchMode"
                :options="availableModes"
                button-variant="light"
                buttons
                name="contact-match-mode-radio"
            ></b-form-radio-group>
        </b-form-group>
        <b-form-group
            id="fieldset-contact-match-mode"
            :description="$t('settings.smart-routing.contact-data.match.description')"
            :label="$t('settings.smart-routing.contact-data.match.label')"
            content-cols-lg="7"
            content-cols-sm
            label-cols-lg="3"
            label-cols-sm="4"
        >
            <div v-for="(line, index) in contactMatchLines" class="contact-line align-items-start mb-1">
                <b-form-select v-model="line.key" :options="contactColumnNameOptions" class="contact-line-key" />

                <b-form-radio-group
                    :id="`contact-match-mode-line-${index}-radio`"
                    v-model="line.compareMode"
                    :name="`contact-match-mode-line-${index}-radio`"
                    :options="getLineOptions(line.key)"
                    button-variant="light"
                    buttons
                    class="ml-1 mr-1"
                ></b-form-radio-group>

                <template>
                    <b-form-select
                        v-if="line.key === 'countryCode'"
                        v-model="line.value"
                        :options="countries"
                        text-field="name"
                        value-field="code"
                    />
                    <contact-data-labels v-if="line.key === 'labels'" v-model="line.value" />
                    <b-form-input v-else v-model="line.value" />
                </template>

                <b-button class="ml-auto" variant="flat-light" @click="contactMatchLines.splice(index, 1)">
                    <font-awesome-icon :icon="['fas', 'times']" />
                </b-button>
            </div>
        </b-form-group>
    </div>
</template>
<script>
import { BFormGroup, BFormInput, BFormRadioGroup, BFormSelect, BInputGroup, BButton } from "bootstrap-vue";
import { ValidationProvider } from "vee-validate";
import useContactMetadata from "@/composables/useContactMetadata";
import i18n from "@/libs/i18n";
import { watch, watchEffect, ref, computed } from "@vue/composition-api";
import { getCountriesAsArray } from "@/utils/countries";
import useRoutes from "@/views/modules/settings/smart-routing/useRoutes";
import ContactDataLabels from "@/views/modules/settings/smart-routing/components/contactDataLabels.vue";

export default {
    components: {
        BFormGroup,
        BFormInput,
        BFormRadioGroup,
        BFormSelect,
        BInputGroup,
        BButton,
        ContactDataLabels,
        ValidationProvider,
    },
    setup() {
        const countries = getCountriesAsArray();
        const availableModes = [
            {
                text: i18n.t("settings.smart-routing.match-mode.one"),
                value: "OR",
            },
            {
                text: i18n.t("settings.smart-routing.match-mode.all"),
                value: "AND",
            },
        ];
        const { contactColumnNames, metadataColumnNames } = useContactMetadata();
        const { currentRoute, isLoading } = useRoutes();
        const contactEntries = ref({});
        const contactMatchLines = ref([
            {
                key: "",
                compareMode: "equals",
                value: "",
            },
        ]); // Start with an empty line by default

        const contactColumnNameOptions = computed(() => {
            return [
                ...contactColumnNames.value.map((columnName) => {
                    const name = columnName.replace("contact.", "");
                    const isMetadataColumn = metadataColumnNames.value.some((column) => column === columnName);

                    return {
                        text: isMetadataColumn ? name : i18n.t(`settings.smart-routing.contact-data.column.${name}`),
                        value: name,
                    };
                }),
                {
                    text: i18n.t(`settings.smart-routing.contact-data.column.country`),
                    value: "countryCode",
                },
                {
                    text: i18n.t(`settings.smart-routing.contact-data.column.labels`),
                    value: "labels",
                },
            ];
        });

        const parseContactData = () => {
            if (!currentRoute.value.contactDetailsToMatch) {
                return;
            }

            contactEntries.value = Object.entries(JSON.parse(currentRoute.value.contactDetailsToMatch));
            contactMatchLines.value = contactEntries.value
                .map(([key, values]) => {
                    return values.map((value) => ({
                        key,
                        compareMode: value.mode,
                        value: value.value,
                    }));
                })
                .flat(1);
            currentRoute.value.contactMatchLines = contactMatchLines.value;
        };

        if (isLoading.value) {
            const stop = watch(isLoading, () => {
                if (!isLoading.value) {
                    parseContactData();

                    // NOTE: The route might not have a value yet so we need to watch untill it is loaded and then we'll parse the contactDetails object
                    // However we need to stop this watcher once we parsed it because else it will make an infinite loop in conjunction with the other watcher
                    // which is needed in order to add a new empty row once there is none anymore
                    stop();
                }
            });
        } else {
            parseContactData();
        }

        watchEffect(() => {
            if (contactMatchLines.value) {
                // Create a new empty line if there are no empty lines
                const emptyLine = contactMatchLines.value.find((matchLine) => !matchLine.key && !matchLine.value);
                if (!emptyLine) {
                    contactMatchLines.value = [
                        ...contactMatchLines.value,
                        {
                            key: "",
                            compareMode: "equals",
                            value: "",
                        },
                    ];
                }
                currentRoute.value.contactMatchLines = contactMatchLines.value;
            }
        });

        return {
            availableModes,
            contactColumnNameOptions,
            contactMatchLines,
            countries,
            currentRoute,
        };
    },
    methods: {
        getLineOptions(key) {
            switch (key) {
                case "countryCode":
                    return [
                        {
                            text: i18n.t("settings.smart-routing.contact-data.match-mode.equals"),
                            value: "equals",
                        },
                        {
                            text: i18n.t("settings.smart-routing.contact-data.match-mode.contains"),
                            value: "contains",
                            disabled: true,
                        },
                    ];
                default:
                    return [
                        {
                            text: i18n.t("settings.smart-routing.contact-data.match-mode.equals"),
                            value: "equals",
                        },
                        {
                            text: i18n.t("settings.smart-routing.contact-data.match-mode.contains"),
                            value: "contains",
                        },
                    ];
            }
        },
    },
};
</script>
<style lang="scss" scoped>
.contact-line {
    display: flex;
    flex-direction: row;

    .contact-line-key {
        flex: 0 0 33%;
    }
}
</style>
